import { useAuth0 } from "@auth0/auth0-react";
import { Button, Drawer, Table, Popover, Tag } from "antd";
import parse from "html-react-parser";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SortableList, { SortableItem } from "react-easy-sort";
import { ReactSVG } from "react-svg";
import { useContextState } from "../../../context/ContextProvider";
import Filter from "../../../shared/Filter";
import Search from "../../../shared/Search";
import {
  getModuleData,
  getQboModuleData,
  getModuleDataWithSearch,
  getQboModuleDataWithSearch,
} from "../../../utils/organization";
import { formatNumber } from "../../../utils/helperFunctions";
import "./folderContentDetails.scss";

const FolderContentDetails = ({ orgId, date, moduleName, logId, onBack, type = "xero" }) => {
  const { isLoading, getIdTokenClaims } = useAuth0();

  const [tableHeaders, setTableHeaders] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const { breadCrumb, setBreadCrumb } = useContextState();
  const navigate = useNavigate();

  // Search mode state
  const [searchMode, setSearchMode] = useState({
    isActive: false,
    query: "",
    total: 0,
    offset: 0,
    hasMore: false,
  });
  const [currentChunk, setCurrentChunk] = useState({
    items: [],
    offset: 0,
  });

  // File-based pagination state (normal mode)
  const [fileBasedMode, setFileBasedMode] = useState({
    total: 0,
    currentFileIndex: 0,
    itemsPerFile: 5000,
  });
  const [currentFile, setCurrentFile] = useState({
    items: [],
    fileIndex: 0,
  });

  // Legacy mode state (for backward compatibility with unsplit files)
  const [legacyMode, setLegacyMode] = useState({
    isLegacy: false,
    allItems: [], // Store all items from legacy file
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const chunkCache = useRef(new Map()); // For search mode chunks
  const fileCache = useRef(new Map()); // For normal mode files
  const searchDebounceTimer = useRef(null);

  const SERVER_CHUNK_SIZE = 5000;
  const ITEMS_PER_FILE = 5000;

  // Handle responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Clean up
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helper function: Generate cache key
  const getChunkCacheKey = useCallback((search, offset) => {
    return `${search}-${offset}`;
  }, []);

  // Helper function: Calculate required chunk offset based on global item index (search mode)
  const calculateRequiredChunk = useCallback((globalItemIndex) => {
    return Math.floor(globalItemIndex / SERVER_CHUNK_SIZE) * SERVER_CHUNK_SIZE;
  }, []);

  // Helper function: Calculate required file index based on global item index (normal mode)
  const calculateRequiredFileIndex = useCallback((globalItemIndex) => {
    return Math.floor(globalItemIndex / ITEMS_PER_FILE);
  }, []);

  // Helper function: Fetch chunk from server (search mode only)
  const fetchChunk = useCallback(async (searchQuery, offset) => {
    try {
      const token = await getIdTokenClaims();
      const cacheKey = getChunkCacheKey(searchQuery, offset);

      // Check cache first
      if (chunkCache.current.has(cacheKey)) {
        const cachedData = chunkCache.current.get(cacheKey);
        setCurrentChunk({
          items: cachedData.content,
          offset: offset,
        });
        return cachedData;
      }

      // Fetch from server
      setTableDataLoading(true);
      const searchApi = type === "qbo" ? getQboModuleDataWithSearch : getModuleDataWithSearch;
      const response = await searchApi(
        token.__raw,
        logId,
        date,
        moduleName,
        searchQuery,
        offset
      );

      const data = response.data;

      // Cache the result
      chunkCache.current.set(cacheKey, data);

      // Update current chunk
      setCurrentChunk({
        items: data.content,
        offset: offset,
      });

      // Update search mode state
      setSearchMode({
        isActive: true,
        query: searchQuery,
        total: data.total || 0,
        offset: offset,
        hasMore: data.has_more || false,
      });

      // Update headers if they exist (QBO may return `header` instead of `headers`)
      const responseHeaders = normalizeResponseHeaders(data);
      if (responseHeaders.length) {
        const headers = portTableHeaders(responseHeaders);
        setTableHeaders(headers);
        setVisibleColumns(headers);
      }

      setTableDataLoading(false);
      return data;
    } catch (error) {
      console.error("Error fetching chunk:", error);
      setTableDataLoading(false);
      throw error;
    }
  }, [getIdTokenClaims, logId, date, moduleName, getChunkCacheKey]);

  // Helper function: Fetch file from server (normal mode file-based pagination)
  const fetchFile = useCallback(async (fileIndex) => {
    try {
      const token = await getIdTokenClaims();
      const cacheKey = `file-${fileIndex}`;

      // Check cache first
      if (fileCache.current.has(cacheKey)) {
        const cachedData = fileCache.current.get(cacheKey);
        setCurrentFile({
          items: cachedData.content,
          fileIndex: fileIndex,
        });
        return cachedData;
      }

      // Fetch from server
      setTableDataLoading(true);
      const fetchApi = type === "qbo" ? getQboModuleData : getModuleData;
      const response = await fetchApi(
        token.__raw,
        orgId,
        date,
        moduleName,
        fileIndex
      );

      const data = response.data;

      // Cache the result
      fileCache.current.set(cacheKey, data);

      // Detect if all data is in one response (legacy or small dataset)
      // Scenarios:
      // 1. legacy: true → Legacy unsplit file (>5000 items, all in one response)
      // 2. content.length === total → Small dataset (<5000 items, all in one response)
      // 3. content.length < total → Modern split files (fetch more as needed)
      const hasAllData = data.legacy === true ||
        (data.content && data.content.length === data.total);

      if (hasAllData) {
        // Legacy/Small Dataset Mode: All data received, no more API calls needed
        // Store all items for client-side pagination
        setLegacyMode({
          isLegacy: true,
          allItems: data.content,
        });

        // For files with all data, we don't need file-based fetching
        setFileBasedMode({
          total: data.total || 0,
          currentFileIndex: 0,
          itemsPerFile: data.total || 0, // All items in one "file"
        });
      } else {
        // Modern Mode: File-based pagination (5000-item split files)
        // More files exist, fetch as needed during navigation
        setLegacyMode({
          isLegacy: false,
          allItems: [],
        });

        // Update current file
        setCurrentFile({
          items: data.content,
          fileIndex: fileIndex,
        });

        // Update file-based mode state
        setFileBasedMode({
          total: data.total || 0,
          currentFileIndex: fileIndex,
          itemsPerFile: data.items_per_file || ITEMS_PER_FILE,
        });
      }

      // Update headers if they exist (QBO may return `header` instead of `headers`)
      const responseHeaders = normalizeResponseHeaders(data);
      if (responseHeaders.length) {
        const headers = portTableHeaders(responseHeaders);
        setTableHeaders(headers);
        setVisibleColumns(headers);
      }

      // Update pagination total
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
      }));

      setTableDataLoading(false);
      return data;
    } catch (error) {
      console.error("Error fetching file:", error);
      setTableDataLoading(false);
      throw error;
    }
  }, [getIdTokenClaims, orgId, date, moduleName, type]);

  // Helper function: Get current page items (handles search mode, legacy mode, and file-based normal mode)
  const getCurrentPageItems = useCallback(() => {
    const { current: currentPage, pageSize } = pagination;
    const globalItemIndex = (currentPage - 1) * pageSize;

    if (searchMode.isActive) {
      // Search mode: paginate within current chunk
      const indexWithinChunk = globalItemIndex - currentChunk.offset;
      return (currentChunk.items || []).slice(
        indexWithinChunk,
        indexWithinChunk + pageSize
      );
    }

    // Legacy mode: client-side pagination from all items
    if (legacyMode.isLegacy) {
      return (legacyMode.allItems || []).slice(
        globalItemIndex,
        globalItemIndex + pageSize
      );
    }

    // Normal mode: file-based pagination
    // Calculate index within current file
    const fileStartIndex = currentFile.fileIndex * ITEMS_PER_FILE;
    const indexWithinFile = globalItemIndex - fileStartIndex;

    return (currentFile.items || []).slice(
      indexWithinFile,
      indexWithinFile + pageSize
    );
  }, [searchMode.isActive, legacyMode.isLegacy, legacyMode.allItems, pagination, currentChunk.items, currentChunk.offset, currentFile.items, currentFile.fileIndex]);

  // Debounced search handler
  const handleSearchChange = useCallback((value) => {
    // Clear previous timer
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    // If empty, exit search mode and return to file-based pagination
    if (!value || value.trim() === "") {
      setSearchMode({
        isActive: false,
        query: "",
        total: 0,
        offset: 0,
        hasMore: false,
      });
      setCurrentChunk({
        items: [],
        offset: 0,
      });
      setPagination(prev => ({
        ...prev,
        current: 1,
        total: fileBasedMode.total,
      }));
      return;
    }

    // Debounce the search
    searchDebounceTimer.current = setTimeout(async () => {
      try {
        // Reset to page 1 and fetch first chunk
        setPagination(prev => ({ ...prev, current: 1 }));
        const data = await fetchChunk(value.trim(), 0);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
        }));
      } catch (error) {
        console.error("Search error:", error);
      }
    }, 300);
  }, [fetchChunk, fileBasedMode.total]);

  // Handle table pagination change (page, pageSize, sorter)
  const handleTableChange = useCallback(async (paginationConfig, filters, sorter) => {
    const newPage = paginationConfig.current;
    const newPageSize = paginationConfig.pageSize;
    const globalItemIndex = (newPage - 1) * newPageSize;
    const pageSizeChanged = newPageSize !== pagination.pageSize;

    if (!searchMode.isActive) {
      // Legacy mode: client-side pagination only, no file fetching needed
      if (legacyMode.isLegacy) {
        setPagination({
          current: pageSizeChanged ? 1 : newPage,
          pageSize: newPageSize,
          total: fileBasedMode.total,
        });
        return;
      }

      // Normal mode: file-based pagination
      const requiredFileIndex = calculateRequiredFileIndex(globalItemIndex);

      if (pageSizeChanged) {
        // PageSize changed: reset to page 1 and fetch file 0 if needed
        setPagination({
          current: 1,
          pageSize: newPageSize,
          total: fileBasedMode.total,
        });

        if (currentFile.fileIndex !== 0) {
          await fetchFile(0);
        }
        return;
      }

      // Check if we need to fetch a new file
      if (requiredFileIndex !== currentFile.fileIndex) {
        await fetchFile(requiredFileIndex);
      }

      // Update pagination state
      setPagination({
        current: newPage,
        pageSize: newPageSize,
        total: fileBasedMode.total,
      });
      return;
    }

    // Search mode: check if we need to fetch a new chunk
    const requiredChunkOffset = calculateRequiredChunk(globalItemIndex);

    if (pageSizeChanged) {
      // PageSize changed: reset to page 1 and recalculate
      setPagination({
        current: 1,
        pageSize: newPageSize,
        total: searchMode.total,
      });

      // Fetch chunk starting at offset 0
      if (requiredChunkOffset !== currentChunk.offset) {
        await fetchChunk(searchMode.query, 0);
      }
      return;
    }

    // Check if we need to fetch a new chunk
    if (requiredChunkOffset !== currentChunk.offset) {
      await fetchChunk(searchMode.query, requiredChunkOffset);
    }

    // Update pagination state
    setPagination({
      current: newPage,
      pageSize: newPageSize,
      total: searchMode.total,
    });
  }, [searchMode, currentChunk, pagination.pageSize, fileBasedMode.total, currentFile.fileIndex, calculateRequiredChunk, calculateRequiredFileIndex, fetchChunk, fetchFile]);

  // Initial data load (non-search mode) - fetch first file (file_index=0)
  useEffect(() => {
    if (!isLoading) {
      (async () => {
        try {
          await fetchFile(0);
        } catch (e) {
          console.error("Error loading initial data:", e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  const handleColumnChange = (checked, columnKey) => {
    if (checked) {
      setVisibleColumns((prev) => [
        ...prev,
        tableHeaders.find((col) => col.key === columnKey),
      ]);
    } else {
      setVisibleColumns((prev) => prev.filter((col) => col.key !== columnKey));
    }
  };

  const reorderColumns = (columns, oldIndex, newIndex) => {
    if (oldIndex === newIndex) return columns;
    const updatedColumns = [...columns];
    const movedColumn = updatedColumns.splice(oldIndex, 1)[0];
    updatedColumns.splice(newIndex, 0, movedColumn);
    return updatedColumns;
  };

  const onSortEnd = (oldIndex, newIndex) => {
    if (visibleColumns.length) {
      setTableHeaders((prev) => reorderColumns(prev, oldIndex, newIndex));
      setVisibleColumns((prev) => reorderColumns(prev, oldIndex, newIndex));
    }
  };

  const HIDDEN_COLUMNS = ["DateString", "DueDateString", "CreatedDateUTCString"];

  const formatComplexValue = (val, colTitle = "") => {
    if (val === null || val === undefined) {
      return <span>-</span>;
    }

    // 1. Primitive types
    if (typeof val !== "object") {
      if (typeof val === "boolean") {
        return <span>{val ? "Yes" : "No"}</span>;
      }
      return <span>{String(val)}</span>;
    }

    // 2. Arrays
    if (Array.isArray(val)) {
      if (val.length === 0) return <span>-</span>;

      // Array of primitives
      if (typeof val[0] !== "object") {
        return <span>{val.map((v) => String(v)).join(", ")}</span>;
      }

      // Name-Value pairs: [{ Name: "X", Value: "Y" }] or [{ DefinitionId: "1", StringValue: "..." }]
      if (val[0] && ("Name" in val[0] || "Key" in val[0])) {
        const formatted = val
          .map((item) => `${item.Name || item.Key || ""}: ${item.Value || item.StringValue || ""}`.trim())
          .filter(Boolean)
          .join(" • ");
        if (formatted) return <span>{formatted}</span>;
      }

      // Array of references: [{ name: "...", value: "..." }]
      if (val[0] && ("name" in val[0] || "value" in val[0])) {
        return <span>{val.map((item) => item.name || item.value || "").filter(Boolean).join(", ")}</span>;
      }

      // Line items (Invoices, Bills, Purchases, etc.)
      if (val[0] && ("DetailType" in val[0] || "Amount" in val[0])) {
        const totalAmount = val.reduce((acc, curr) => acc + (Number(curr.Amount) || 0), 0);
        const summaryText = `${val.length} Line${val.length > 1 ? "s" : ""}${
          totalAmount ? ` (${formatNumber(totalAmount)})` : ""
        }`;

        return (
          <Popover
            placement="topLeft"
            title={`${colTitle || "Line Items"} (${val.length})`}
            content={
              <div style={{ maxWidth: 380, maxHeight: 260, overflowY: "auto" }}>
                {val.map((line, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "6px 0",
                      borderBottom: idx < val.length - 1 ? "1px solid #f0f0f0" : "none",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ fontWeight: 600, display: "flex", justifyContent: "space-between" }}>
                      <span>{line.Description || line.DetailType || `Line #${idx + 1}`}</span>
                      {line.Amount !== undefined && (
                        <span style={{ color: "#1677ff" }}>{formatNumber(line.Amount)}</span>
                      )}
                    </div>
                    {line.AccountBasedExpenseLineDetail?.AccountRef?.name && (
                      <div style={{ color: "#8c8c8c" }}>
                        Account: {line.AccountBasedExpenseLineDetail.AccountRef.name}
                      </div>
                    )}
                    {line.SalesItemLineDetail?.ItemRef?.name && (
                      <div style={{ color: "#8c8c8c" }}>
                        Item: {line.SalesItemLineDetail.ItemRef.name}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            }
          >
            <Tag color="blue" style={{ cursor: "pointer" }}>
              {summaryText}
            </Tag>
          </Popover>
        );
      }

      // General object array: render count with popover preview
      return (
        <Popover
          placement="topLeft"
          title={`${colTitle || "Details"} (${val.length} items)`}
          content={
            <pre style={{ maxWidth: 360, maxHeight: 240, overflow: "auto", fontSize: 11, margin: 0 }}>
              {JSON.stringify(val, null, 2)}
            </pre>
          }
        >
          <Tag style={{ cursor: "pointer" }}>{val.length} items</Tag>
        </Popover>
      );
    }

    // 3. QBO Reference entities: { name: "...", value: "..." }
    if ("name" in val || "value" in val) {
      return <span>{val.name || val.value || "-"}</span>;
    }

    // 4. Contact info: Phone, Email, Web
    if ("FreeFormNumber" in val) {
      return <span>{val.FreeFormNumber || "-"}</span>;
    }
    if ("Address" in val && typeof val.Address === "string") {
      return <span>{val.Address || "-"}</span>;
    }
    if ("URI" in val) {
      return val.URI ? (
        <a href={val.URI} target="_blank" rel="noopener noreferrer">
          {val.URI}
        </a>
      ) : (
        <span>-</span>
      );
    }

    // 5. Physical Addresses (CompanyAddr, BillAddr, ShipAddr, LegalAddr)
    if (
      "Line1" in val ||
      "Line2" in val ||
      "City" in val ||
      "PostalCode" in val ||
      "CountrySubDivisionCode" in val ||
      "Country" in val
    ) {
      const parts = [
        val.Line1,
        val.Line2,
        val.Line3,
        val.Line4,
        val.City,
        val.CountrySubDivisionCode,
        val.PostalCode,
        val.Country,
      ].filter(Boolean);

      return <span>{parts.length ? parts.join(", ") : "-"}</span>;
    }

    // If address only has internal { Id: "3" } and nothing else, show dash instead of {"Id":"3"}
    const nonIdKeys = Object.keys(val).filter((k) => k !== "Id" && k !== "domain" && k !== "sparse");
    if (nonIdKeys.length === 0) {
      return <span>-</span>;
    }

    // 6. MetaData: CreateTime, LastUpdatedTime
    if ("CreateTime" in val || "LastUpdatedTime" in val) {
      const createTime = val.CreateTime ? `Create: ${val.CreateTime.replace("T", " ").split("+")[0]}` : "";
      const updatedTime = val.LastUpdatedTime
        ? `Updated: ${val.LastUpdatedTime.replace("T", " ").split("+")[0]}`
        : "";
      return <span>{[createTime, updatedTime].filter(Boolean).join(" • ") || "-"}</span>;
    }

    // 7. Simple flat key-value object (e.g. Preferences booleans/strings/numbers)
    const isSimpleObject = nonIdKeys.every(
      (k) => typeof val[k] !== "object" || val[k] === null
    );

    if (isSimpleObject && nonIdKeys.length <= 4) {
      const formatted = nonIdKeys
        .map((k) => `${k}: ${typeof val[k] === "boolean" ? (val[k] ? "Yes" : "No") : val[k]}`)
        .join(", ");
      return <span>{formatted}</span>;
    }

    // 8. Complex / deeply nested object: render concise summary pill with hover popover
    return (
      <Popover
        placement="topLeft"
        title={colTitle || "Details"}
        content={
          <pre style={{ maxWidth: 380, maxHeight: 260, overflow: "auto", fontSize: 11, margin: 0 }}>
            {JSON.stringify(val, null, 2)}
          </pre>
        }
      >
        <span style={{ cursor: "pointer", color: "#1677ff", fontSize: "12px" }}>
          View Details ({nonIdKeys.length} fields)
        </span>
      </Popover>
    );
  };

  const portTableHeaders = (headers) => {
    let temp = [];

    (headers || []).filter((item) => !HIDDEN_COLUMNS.includes(item)).forEach((item) => {
      // Calculate minimum width based on header text length
      // Each character needs ~8-10px, plus padding
      const minWidth = Math.max(120, item.length * 10 + 40);

      temp.push({
        title: item,
        dataIndex: item.replace(/\s+/g, ""),
        key: item,
        width: minWidth,
        ellipsis: true, // Enable text truncation with tooltip
        render: (text, record) => {
          const lowerItem = item?.toLowerCase() || "";

          // Complex/nested objects (QBO addresses, references, metadata, lines, etc.)
          if (text && typeof text === "object") {
            return formatComplexValue(text, item);
          }

          if (lowerItem === "view" || lowerItem === "url" || lowerItem === "download") {
            if (typeof text === "string") {
              return parse(text);
            }
          }

          // Skip number formatting for non-financial numeric columns
          if (
            lowerItem.includes("code") ||
            lowerItem.includes("id") ||
            lowerItem.includes("year") ||
            lowerItem.includes("phone") ||
            lowerItem.includes("zip")
          ) {
            return <span>{text}</span>;
          }

          // Format numeric values with commas and decimals
          const formattedText = formatNumber(text);
          return <span>{formattedText}</span>;
        },
      });
    });

    return temp;
  };

  const normalizeResponseHeaders = (data) => {
    const raw = data?.headers ?? data?.header;
    if (Array.isArray(raw) && raw.length) return raw;

    const firstRow = Array.isArray(data?.content) ? data.content[0] : null;
    if (firstRow && typeof firstRow === "object") {
      return Object.keys(firstRow);
    }
    return [];
  };

  const showFilterDrawer = () => {
    setFilterDrawerOpen(true);
  };

  const onFilterDrawerClose = () => {
    setFilterDrawerOpen(false);
  };

  // Determine table scroll settings based on device type
  const getTableScrollConfig = () => {
    if (isMobile) {
      // Enable horizontal scrolling on mobile with minimum width
      return { x: 800 };
    } else {
      return { x: "max-content" };
    }
  };

  const handleBreadCrumbClick = (index) => {
    setBreadCrumb(breadCrumb.slice(0, index + 1));

    const stepsBack = breadCrumb.length - 1 - index;

    if (stepsBack > 0) {
      navigate(-stepsBack);
    }
  }

  return (
    <div>
      <div>
        {isMobile ? (
          <>
            <Button onClick={showFilterDrawer} className="w-full md:w-auto">
              Manage Columns
            </Button>
            <Drawer
              title="Manage Columns"
              placement="right"
              onClose={onFilterDrawerClose}
              open={filterDrawerOpen}
            >
              <Filter
                text=""
                filterList={tableHeaders}
                onSortEnd={onSortEnd}
                handleColumnChange={handleColumnChange}
                visibleFilterList={visibleColumns}
              />
            </Drawer>
          </>
        ) : (
          <div className="flex justify-between items-center my-2">
            <div className="flex gap-3 items-center">
              <div className="flex items-center gap-2">
                <Button
                  onClick={onBack}
                  className="flex items-center"
                  type="default"
                >
                  <ReactSVG src="/assets/icons/arrow-left.svg" />
                  <span className="ml-2">Back</span>
                </Button>
              </div>

              <Filter
                text="Manage Columns"
                filterList={tableHeaders}
                onSortEnd={onSortEnd}
                handleColumnChange={handleColumnChange}
                visibleFilterList={visibleColumns}
              />

              <div className="flex items-center">
                {breadCrumb.map((item, index) => {
                  const isLastItem = index === breadCrumb.length - 1;
                  return (
                    <div key={index} className="flex items-center">
                      <p className={"cursor-pointer " + (isLastItem ? "font-semibold" : "")} onClick={() => handleBreadCrumbClick(index)}>
                        {item.label}
                      </p>
                      {!isLastItem && <span className="mx-2">&gt;</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <Search
                fullWidth={true}
                placeholder={
                  isMobile
                    ? "Search..."
                    : "Search by module, record or file type..."
                }
                handleChange={handleSearchChange}
              />
              {searchMode.isActive && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Showing {Math.min((pagination.current - 1) * pagination.pageSize + 1, searchMode.total)}-
                  {Math.min(pagination.current * pagination.pageSize, searchMode.total)} of {searchMode.total} results
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <SortableList
        onSortEnd={onSortEnd}
        className="sortable-header"
        draggedItemClassName="dragged"
        direction="horizontal"
      >
        <Table
          sticky={true}
          size={isMobile ? "small" : "default"}
          scroll={getTableScrollConfig()}
          dataSource={getCurrentPageItems()}
          columns={visibleColumns.map((col) => ({
            ...col,
            title: (
              <SortableItem key={col.key}>
                <span className="draggable-column">{col.title}</span>
              </SortableItem>
            ),
            sorter: (a, b) => {
              const aValue = a[col.dataIndex];
              const bValue = b[col.dataIndex];

              if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
              }

              return String(aValue).localeCompare(String(bValue));
            },
            sortDirections: ['ascend', 'descend', 'ascend'],
          }))}
          loading={tableDataLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            responsive: true,
            position: ["bottomCenter"],
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={handleTableChange}
        />
      </SortableList>
    </div>
  );
};

export default FolderContentDetails;
